import { addDays, isAfter, startOfDay } from "date-fns";
import { DateRange } from "react-day-picker";
import { calculateCalendarDuration } from "@/lib/calendar";

/** Durée minimum par défaut affichée / appliquée côté client (aligné ReservationCard, Step1). */
export const DEFAULT_VEHICLE_MIN_RENTAL_DAYS = 3;

/**
 * Durée minimum de location pour un véhicule (config agence).
 * Utilise la valeur DB si présente, sinon le défaut produit (3 jours).
 */
export const getVehicleMinimumRentalDays = (
  minimumRentalDays?: number | null
): number => {
  if (
    minimumRentalDays != null &&
    Number.isFinite(minimumRentalDays) &&
    minimumRentalDays >= 1
  ) {
    return Math.floor(minimumRentalDays);
  }
  return DEFAULT_VEHICLE_MIN_RENTAL_DAYS;
};

/** Date de fin minimale : start + N nuits calendaires (aligné SQL end - start). */
export const getMinRentalEndDateForDays = (start: Date, minDays: number): Date =>
  addDays(startOfDay(start), minDays);

/**
 * Garantit une plage d'au moins minDays jours de location.
 * Si la fin choisie est trop proche, elle est étendue automatiquement.
 */
export const enforceMinVehicleRentalRange = (
  from: Date,
  to: Date,
  minDays: number,
  maxSelectableDate: Date
): DateRange => {
  const start = startOfDay(from);
  let end = startOfDay(to);
  const effectiveMin = Math.max(1, minDays);

  if (
    calculateCalendarDuration({ startDate: start, endDate: end }) < effectiveMin
  ) {
    end = getMinRentalEndDateForDays(start, effectiveMin);
  }

  if (isAfter(end, maxSelectableDate)) {
    end = maxSelectableDate;
  }

  return { from: start, to: end };
};

export const isVehicleRentalRangeValid = (
  from: Date | undefined,
  to: Date | undefined,
  minDays: number
): boolean => {
  if (!from || !to) return false;
  return (
    calculateCalendarDuration({ startDate: from, endDate: to }) >=
    Math.max(1, minDays)
  );
};

/**
 * True when the user's chosen end is shorter than the vehicle minimum
 * (calendar will auto-extend). Used only for UX messaging — not for pricing.
 */
export const wasMinRentalRangeExtended = (
  from: Date,
  chosenTo: Date,
  minDays: number
): boolean => {
  const effectiveMin = Math.max(1, minDays);
  return (
    calculateCalendarDuration({
      startDate: startOfDay(from),
      endDate: startOfDay(chosenTo),
    }) < effectiveMin
  );
};
